import { useCallback, useMemo } from 'react';
import { makeStartIterationInstruction, makeStopIterationInstruction } from '@gonfalon/experiments/instructions';
import type { Experiment } from '@gonfalon/experiments/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useUpdateExperiment } from '@gonfalon/rest-api/hooks/useUpdateExperiment';
import { holdoutsDetailRoot } from '@gonfalon/rest-api/queries/holdouts';
import { toastQueue } from '@launchpad-ui/components';

import { invalidateExperimentQueries } from '#components/experimentation/common/utils/invalidateExperimentQueries';

export const useUpdateExperimentStatusCallbacks = ({
  experiment,
  projectKey,
  environmentKey,
}: {
  experiment: Experiment | undefined;
  projectKey: string;
  environmentKey: string;
}) => {
  const { mutateAsync: updateExperimentMutationAsync, isPending: isUpdateExperimentPending } = useUpdateExperiment({
    shouldInvalidateQueries: false,
  });

  if (!experiment) {
    throw new Error('Experiment not found');
  }

  const isMAB = experiment?.type === 'mab';
  const isHoldout = experiment?.type === 'holdout';

  const startExperimentIteration = useCallback(
    async (changeJustification?: string, forceCreateIteration?: boolean, onStartSuccess?: () => void) => {
      try {
        await updateExperimentMutationAsync(
          {
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
            body: {
              instructions: [makeStartIterationInstruction(changeJustification)],
            },
          },
          {
            onSuccess: async () => {
              await invalidateExperimentQueries({
                projectKey,
                environmentKey,
                experimentKey: experiment.key,
              });

              onStartSuccess?.();
            },
          },
        );

        if (!forceCreateIteration) {
          await invalidateExperimentQueries({
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
          });
          if (isHoldout) {
            const queryClient = getQueryClient();
            await queryClient.invalidateQueries({
              queryKey: holdoutsDetailRoot,
            });
          }
        }
      } catch (error: unknown) {
        toastQueue.add({
          title: `Failed to start ${isMAB ? 'multi-armed bandit' : 'experiment'} iteration`,
          status: 'error',
          description: error instanceof Error ? error.message : 'An unknown error occurred',
        });
        throw error;
      }
    },
    [updateExperimentMutationAsync, projectKey, environmentKey, experiment, isMAB, isHoldout],
  );

  const stopExperimentIteration = useCallback(
    async (winningTreatmentId: string, winningReason: string) => {
      try {
        await updateExperimentMutationAsync(
          {
            projectKey,
            environmentKey,
            experimentKey: experiment.key,
            body: {
              instructions: [makeStopIterationInstruction(winningTreatmentId, winningReason)],
            },
          },
          {
            onSuccess: async () => {
              await invalidateExperimentQueries({
                projectKey,
                environmentKey,
                experimentKey: experiment.key,
              });
            },
          },
        );
        toastQueue.add({
          title: `${isMAB ? 'Multi-armed bandit' : 'Experiment'} iteration stopped`,
          status: 'success',
        });
      } catch (error: unknown) {
        toastQueue.add({
          title: `Failed to stop ${isMAB ? 'multi-armed bandit' : 'experiment'} iteration`,
          status: 'error',
          description: error instanceof Error ? error.message : 'An unknown error occurred',
        });
        throw error;
      }
    },
    [updateExperimentMutationAsync, projectKey, environmentKey, experiment.key, isMAB],
  );

  return useMemo(
    () => ({
      startExperimentIteration,
      stopExperimentIteration,
      isPending: isUpdateExperimentPending,
    }),
    [startExperimentIteration, stopExperimentIteration, isUpdateExperimentPending],
  );
};
